import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { IdentityUserAri } from '@atlassian/ari/identity/user';
import type { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { JSResourceForInteraction } from '@atlassian/react-async/js-resource-for-interaction';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
	type HomeAssignedListQuery,
} from './assigned-list/__generated__/HomeAssignedListQuery.graphql';
import {
	createJiraIssueCypherQuery,
	createPullRequestCypherQuery,
	createTownsquareCypherQuery,
} from './assigned-list/cypher-query-builders';
import type { ExtraProps, RuntimeProps } from './assigned-list/types';

export type EntryPointParams = {
	/**
	 * The user whose assigned items are fetched. Also used to filter out items the user is no longer
	 * responsible for (for example, a reassigned Jira issue).
	 */
	currentUser: IdentityUserAri;
	/**
	 * Site ARI used to unit scope results.
	 */
	unitId: PlatformSiteAri;
	/**
	 * Number of results to fetch per entity type. Defaults to 25.
	 */
	first?: number;
};

const DEFAULT_LIMIT = 25;

const fetchPolicy: PreloadFetchPolicy = 'store-and-network';

export const homeAssignedListEntryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryReference: HomeAssignedListQuery;
		},
		Record<never, never>,
		RuntimeProps,
		ExtraProps
	>,
	EntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlassian/home-assigned-entrypoint" */ './assigned-list/HomeAssignedListQuery'
			).then((module) => ({ default: module.HomeAssignedListQuery })),
		{ moduleId: 'homeAssignedListEntryPoint' },
	),
	getPreloadProps: ({ currentUser, unitId, first }: EntryPointParams) => {
		const accountId = currentUser.resourceId;

		return {
			queries: {
				queryReference: {
					parameters,
					variables: {
						townsquareQuery: createTownsquareCypherQuery(accountId),
						jiraIssueQuery: createJiraIssueCypherQuery(accountId),
						pullRequestQuery: createPullRequestCypherQuery(accountId),
						queryContext: unitId.toString(),
						first: first ?? DEFAULT_LIMIT,
					},
					options: {
						fetchPolicy,
					},
				},
			},
			extraProps: {
				currentUser,
			},
		};
	},
});
